import React, { useState, useEffect } from "react";
import ReactTable from "react-table";
import { Button, UncontrolledTooltip } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import s from "./styles.scss";
import { faUserEdit, faFileExport } from "@fortawesome/free-solid-svg-icons";
import AnualidadSnapshot from "../../../../types/AnualidadSnapshot";
import { formatIsoDate, formatMoney } from "../../../helpers/formatters";

export interface TableProps {
  page: Page<AnualidadSnapshot>;
  loading: boolean;
  onFetchData: ({ page, pageSize }) => void;
  onExport: () => void;
  onCambiarVendedor: (row: AnualidadSnapshot) => void;
}

export const MemoTable = React.memo(
  ({ page, loading, onFetchData, onExport, onCambiarVendedor }: TableProps) => {
    const [columns, setColumns] = useState(columnsFormatter(onCambiarVendedor));
    const [pageNumber, setPageNumber] = useState<number>(0);
    const [totalPages, setTotalPages] = useState<number>(0);

    useEffect(() => {
      setColumns(columnsFormatter(onCambiarVendedor));
    }, [onCambiarVendedor]);

    useEffect(() => {
      if (page) {
        setPageNumber(page.number || 0);
        setTotalPages(page.totalPages || 0);
      }
    }, [page]);

    return (
      <div>
        <div className="row">
          {!!page && !!page.content && !!page.content.length && (
            <>
              <div className="col-6 p-0" style={{ lineHeight: "40px" }}>
                <span>
                  &nbsp;&nbsp;&nbsp;&nbsp;Anualidades mostradas:{" "}
                  {page.numberOfElements} de {page.totalElements}
                </span>
              </div>
              <div
                className="col-6 p-0 text-right"
                style={{ lineHeight: "40px" }}
              >
                <Button
                  id="btnReportExportCsv"
                  color="black"
                  type="button"
                  onClick={onExport}
                >
                  Exportar <FontAwesomeIcon icon={faFileExport} color="black" />
                </Button>
              </div>
            </>
          )}
        </div>
        <ReactTable
          manual
          data={page ? page.content : []}
          columns={columns}
          pages={totalPages}
          loading={loading}
          onFetchData={onFetchData}
          page={pageNumber}
          defaultPageSize={100}
          showPageSizeOptions={false}
          className="-striped -highlight"
          previousText="Anterior"
          nextText="Siguiente"
          loadingText="Cargando..."
          noDataText="No hay registros"
          pageText="Página"
          ofText="de"
          rowsText="filas"
        />
      </div>
    );
  }
);

function columnsFormatter(onCambiarVendedor: (row: AnualidadSnapshot) => void) {
  return [
    {
      Header: "Cotización",
      id: "idCotizacionItem",
      className: s.tdClear,
      headerClassName: "text-left",
      width: 80,
      accessor: simpleAccessor("idCotizacionItem")
    },
    {
      Header: "Periodo",
      id: "periodo",
      className: s.tdClear,
      headerClassName: "text-left",
      width: 100,
      accessor: dateAccessor("periodo")
    },
    {
      Header: "Folio Factura",
      id: "folioFactura",
      className: s.tdClear,
      headerClassName: "text-left",
      width: 120,
      accessor: simpleAccessor("folioFactura")
    },
    {
      Header: "Vendedor",
      id: "vendedor",
      className: s.tdClear,
      headerClassName: "text-left",
      width: 200,
      accessor: simpleAccessor("vendedor")
    },
    {
      Header: "Descripción",
      id: "descripcion",
      className: s.tdClear,
      headerClassName: "text-left",
      accessor: simpleAccessor("descripcion")
    },
    {
      Header: "Membresía",
      id: "membresiaLink",
      className: s.tdClear,
      headerClassName: "text-left",
      width: 150,
      accessor: (row: AnualidadSnapshot) => {
        const href = `/membresias-socios/${row.idMembresiaSocio}?idClub=${row.idClubBase}&idPublico=${row.idPublico}`;
        const label = `${row.idPublico} de ${row.clubBase}`;

        return (
          <div className={fieldClassName(row)}>
            <a href={href} target="_blank" rel="noreferrer">
              {label}
            </a>
          </div>
        );
      }
    },
    {
      Header: "Precio Venta",
      id: "precioVenta",
      className: s.tdClear,
      headerClassName: "text-left",
      width: 100,
      accessor: moneyAccessor("precioVenta")
    },
    {
      Header: "",
      id: "acciones",
      className: s.tdClear,
      headerClassName: "text-left",
      width: 48,
      accessor: (row: AnualidadSnapshot) => {
        const btnId = `snapshot-${row.idCotizacionItem}`;
        const disabled =
          row.sinSnapshot ||
          row.cotizacionEliminada ||
          row.vendedorContactCenter;
        let alerta = "Debe generar el Snapshot";

        if (row.vendedorContactCenter) {
          alerta =
            "No puede actualizar ventas asociadas a Operadores de Contact Center";
        }

        const color = disabled ? "#777777" : "#4e779b";

        return (
          <div className={fieldClassName(row)}>
            {renderBoton(
              row,
              `${btnId}-vendedor`,
              !disabled,
              onCambiarVendedor,
              !disabled ? "Cambiar Vendedor" : alerta,
              color,
              faUserEdit
            )}
          </div>
        );
      }
    }
  ];
}

function simpleAccessor(key: keyof AnualidadSnapshot) {
  return (row: AnualidadSnapshot) => {
    return <div className={fieldClassName(row)}>{row[key]}&nbsp;</div>;
  };
}

function booleanAccessor(key: keyof AnualidadSnapshot) {
  return (row: AnualidadSnapshot) => {
    return (
      <div className={fieldClassName(row)}>
        {!!row[key] ? "Sí" : "No"}&nbsp;
      </div>
    );
  };
}

function dateAccessor(key: keyof AnualidadSnapshot) {
  return (row: AnualidadSnapshot) => {
    const fecha = row[key] ? formatIsoDate(row[key] as string) : "";
    return <div className={fieldClassName(row)}>{fecha}&nbsp;</div>;
  };
}

function moneyAccessor(accessor: string) {
  return row => {
    const cantidad = Number(row[accessor]);

    return (
      <div className={fieldClassName(row) + " text-right"}>
        {!Number.isNaN(cantidad) && row[accessor] != null
          ? formatMoney(cantidad, 2)
          : "-"}
      </div>
    );
  };
}

function renderBoton(
  row: AnualidadSnapshot,
  id,
  habilitado,
  action,
  tooltip,
  color,
  icon
) {
  if (habilitado) {
    return (
      <span id={id} onClick={() => action(row)} className={s.btn}>
        <FontAwesomeIcon icon={icon} color={color} />
        <UncontrolledTooltip placement="left" target={id}>
          {tooltip}
        </UncontrolledTooltip>
      </span>
    );
  } else {
    return (
      <span className={s.info} id={id}>
        <FontAwesomeIcon icon={icon} color={color} />
        <UncontrolledTooltip placement="left" target={id}>
          {tooltip}
        </UncontrolledTooltip>
      </span>
    );
  }
}

function fieldClassName(row: AnualidadSnapshot) {
  let className = s.field;

  /*if (!!row.sinSnapshot) {
    className += ` ${s.sinSnapshot}`;
  }*/

  if (!row.facturaValida) {
    className += ` ${s.facturaCancelada}`;
  }

  /*if (row.tieneCambios) {
    className += ` ${s.conCambios}`;
  } else if (row.evaluadorModifico) {
    className += ` ${s.conActualizaciones}`;
  }*/

  return className;
}
